/**
 * ADR-0068: Change-Scope dialog. Spec §4 (lines 147–179), §8 (251).
 *
 * Contract (spec §4):
 *   - Admin-only demote is enforced server-side. The dialog still shows the
 *     Personal / Team / Shared radio to admins (and Team/Shared-only for
 *     non-admin promotion callers).
 *   - On open, fetches the references preview via
 *     `GET /tenant/{entity}/:id/scope-references?target_scope=...` for each
 *     target tier the user selects. The Move button stays disabled until
 *     the preview is empty.
 *   - On submit, calls `POST /tenant/{entity}/:id/promote` or `/demote`
 *     depending on direction. `onChanged` fires on success.
 *   - 409 `name_conflict_at_target_scope` renders an inline rename field +
 *     "Rename and move" button. The rename flow is NOT implemented in v1
 *     (it would require a second API call to PATCH the name before retrying —
 *     flagged as §4 follow-up) — the v1 dialog shows the conflict message
 *     and lets the user Cancel + rename manually before reopening.
 *   - 409 `reference_integrity_violation` re-renders the references list
 *     with the fresh violations; Move stays disabled.
 *
 * Accessibility:
 *   - Focus-trap via the existing `.modalOverlay` pattern used across other
 *     console dialogs (ConfirmDialog, KnowledgeReviewModal).
 *   - `aria-labelledby` on the title, Esc closes, Enter submits when Move
 *     is enabled.
 */

import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import {
  useMyGroups,
  type ContentScope,
  type ScopedEntityKind,
  type ScopeReferenceViolation
} from "../lib/scope.js";

/** Hyphenated URL kind for each entity. Must match hub route paths. */
const URL_KIND: Record<ScopedEntityKind, string> = {
  page: "pages",
  menu: "menus",
  pdo: "pdos",
  workflow: "workflows",
  saved_query: "queries",
  study: "studies",
  optimize_model: "optimize/models"
};

type Direction = "promote" | "demote";

export function ChangeScopeDialog(props: {
  entityKind: ScopedEntityKind;
  entityId: string;
  currentScope: ContentScope;
  currentGroupId?: string | null;
  currentOwnerAccountId?: string | null;
  /** Role of the current caller — used to decide which targets are visible. */
  callerRole: "admin" | "builder" | "viewer";
  onClose: () => void;
  onChanged: (updated: { scope: ContentScope; owner_group_id: string | null; owner_account_id: string | null }) => void;
}) {
  const { groups, loading: groupsLoading } = useMyGroups();

  // Target scope selection state. Default: first valid promote/demote target.
  const defaultTarget: ContentScope | null = useMemo(() => {
    if (props.currentScope === "personal") return "team";
    if (props.currentScope === "team") return "shared";
    return "team"; // shared → team by default (demote)
  }, [props.currentScope]);

  const [targetScope, setTargetScope] = useState<ContentScope | null>(defaultTarget);
  const [targetGroupId, setTargetGroupId] = useState<string>("");
  const [violations, setViolations] = useState<ScopeReferenceViolation[]>([]);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameConflict, setNameConflict] = useState<{ existing_id: string; existing_name: string } | null>(null);

  const urlKind = URL_KIND[props.entityKind];

  // Determine direction (promote vs demote) based on current → target.
  const direction: Direction | null = useMemo(() => {
    if (!targetScope) return null;
    const rank: Record<ContentScope, number> = { personal: 0, team: 1, shared: 2 };
    if (rank[targetScope] > rank[props.currentScope]) return "promote";
    if (rank[targetScope] < rank[props.currentScope]) return "demote";
    return null; // same-scope — invalid
  }, [targetScope, props.currentScope]);

  // Fetch references preview whenever targetScope changes.
  useEffect(() => {
    let cancelled = false;
    if (!targetScope || targetScope === props.currentScope) {
      setViolations([]);
      return;
    }
    setChecking(true);
    setError(null);
    (async () => {
      try {
        const r = await apiFetch<{ violations: ScopeReferenceViolation[] }>(
          `/api/tenant/${urlKind}/${props.entityId}/scope-references?target_scope=${targetScope}`
        );
        if (!cancelled) {
          setViolations(r.violations ?? []);
          setChecking(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setChecking(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetScope, urlKind, props.entityId, props.currentScope]);

  // Esc closes dialog.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) {
        props.onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [props, submitting]);

  const canSubmit: boolean =
    !!direction &&
    !!targetScope &&
    !checking &&
    !submitting &&
    violations.length === 0 &&
    // target_group_id required when moving to team
    (targetScope !== "team" || !!targetGroupId);

  const handleSubmit = async () => {
    if (!canSubmit || !direction || !targetScope) return;
    setSubmitting(true);
    setError(null);
    setNameConflict(null);
    const body: Record<string, unknown> = { target_scope: targetScope };
    if (targetScope === "team") body.target_group_id = targetGroupId;
    // Demote-to-personal: hub assigns ownership to the caller. No picker.
    try {
      const r = await apiFetch<{ scope: ContentScope; owner_group_id: string | null; owner_account_id: string | null }>(
        `/api/tenant/${urlKind}/${props.entityId}/${direction}`,
        { method: "POST", body }
      );
      props.onChanged(r);
      props.onClose();
    } catch (e) {
      if (e instanceof ApiRequestError) {
        if (e.code === "reference_integrity_violation") {
          const v = (e.data?.violations as ScopeReferenceViolation[]) ?? [];
          setViolations(v);
          setError("Scope change is blocked by referenced content. Fix the dependencies above, then retry.");
        } else if (e.code === "name_conflict_at_target_scope") {
          const existing_id = (e.data?.existing_id as string) ?? "";
          const existing_name = (e.data?.existing_name as string) ?? "";
          setNameConflict({ existing_id, existing_name });
          setError(
            `A ${labelFor(props.entityKind)} named "${existing_name}" already exists in the target scope. Rename it before moving, or cancel and rename this row.`
          );
        } else {
          setError(e.message || e.code);
        }
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Which scope options to show in the radio. Non-admin callers only see
  // promote targets (wider than current). Admins see all three so they can
  // also demote.
  const options: Array<{ value: ContentScope; label: string; disabled: boolean; help?: string }> = useMemo(() => {
    const out: Array<{ value: ContentScope; label: string; disabled: boolean; help?: string }> = [];
    const isAdmin = props.callerRole === "admin";
    const rank: Record<ContentScope, number> = { personal: 0, team: 1, shared: 2 };
    // Personal target (only visible to admin as demote destination)
    if (isAdmin) {
      out.push({
        value: "personal",
        label: "Personal",
        disabled: rank["personal"] === rank[props.currentScope],
        help: "Owned by a single user, visible to them and their management chain — demoting requires picking a target owner below."
      });
    }
    // Team target
    out.push({
      value: "team",
      label: "Team",
      disabled: rank["team"] === rank[props.currentScope] || groups.length === 0 && !isAdmin,
      help: groups.length === 0 && !isAdmin ? "You are not a member of any team yet." : "Shared within a specific team."
    });
    // Shared target
    out.push({
      value: "shared",
      label: "Shared",
      disabled: rank["shared"] === rank[props.currentScope] || (!isAdmin && props.callerRole !== "builder"),
      help: !isAdmin && props.callerRole !== "builder"
        ? "Requires a builder or admin to publish to shared."
        : "Visible to every member of the tenant."
    });
    return out;
  }, [props.callerRole, props.currentScope, groups.length]);

  return (
    <div
      className="modalOverlay"
      role="presentation"
      onClick={submitting ? undefined : props.onClose}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="changeScopeTitle"
        style={{ maxWidth: 520, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canSubmit && !(e.target instanceof HTMLSelectElement)) {
            e.preventDefault();
            void handleSubmit();
          }
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)" }}>
          <h2 id="changeScopeTitle" style={{ margin: 0, fontSize: 16 }}>
            Change scope
          </h2>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Move this {labelFor(props.entityKind)} to a different visibility tier.
          </div>
        </div>

        <div style={{ padding: "14px 18px", display: "grid", gap: 12 }}>
          <div className="scopeCreateRadio">
            {options.map((opt) => (
              <label
                key={opt.value}
                className={
                  "scopeCreateRadioOption" +
                  (targetScope === opt.value ? " isActive" : "") +
                  (opt.disabled ? " isDisabled" : "")
                }
              >
                <input
                  type="radio"
                  name="target_scope"
                  checked={targetScope === opt.value}
                  disabled={opt.disabled}
                  onChange={() => {
                    setTargetScope(opt.value);
                    setTargetGroupId("");
                    setNameConflict(null);
                  }}
                />
                <div>
                  <div className="scopeCreateRadioLabel">{opt.label}</div>
                  {opt.help ? <div className="scopeCreateRadioHelp">{opt.help}</div> : null}
                </div>
              </label>
            ))}
          </div>

          {targetScope === "team" && (
            <label className="field">
              <span className="label">Team</span>
              <select
                className="select"
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
                disabled={groupsLoading}
              >
                <option value="">Select a team…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {direction === "demote" && targetScope === "personal" && (
            <div className="help">
              The {labelFor(props.entityKind)} will become your personal content.
            </div>
          )}

          {violations.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                Blocked by {violations.length} reference{violations.length === 1 ? "" : "s"}:
              </div>
              <div className="scopeRefList">
                {violations.map((v) => (
                  <div key={`${v.ref_kind}:${v.ref_id}`} className="scopeRefItem">
                    <div>
                      <span className="scopeRefKind">{v.ref_kind}</span>{" "}
                      <strong>{v.ref_name ?? "(unnamed)"}</strong>
                    </div>
                    <span className="muted" style={{ fontSize: 11 }}>
                      scope: {v.ref_scope}
                    </span>
                  </div>
                ))}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Widen the referenced rows first, then retry.
              </div>
            </div>
          )}

          {error ? <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div> : null}

          {nameConflict ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>
              Conflicting row: <code className="mono">{nameConflict.existing_id}</code>
            </div>
          ) : null}
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8
          }}
        >
          <button className="btn" onClick={props.onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btnPrimary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {submitting ? "Moving…" : checking ? "Checking…" : "Move"}
          </button>
        </div>
      </div>
    </div>
  );
}

function labelFor(kind: ScopedEntityKind): string {
  switch (kind) {
    case "page":
      return "page";
    case "menu":
      return "menu";
    case "pdo":
      return "PDO";
    case "optimize_model":
      return "solver config";
    case "workflow":
      return "schedule";
    case "saved_query":
      return "query";
    case "study":
      return "study";
  }
}
